import { useRef } from 'react';
import { useTranslator } from '@picsart/rc/services/localization';

import InputsSection from '@/components/InputsSection';
import useAuth from '@/hooks/useAuth';
import { useAppStore, type IAppStore } from '@/store';
import isFormValid from '@/utils/isFormValid';
import { useProjectCredits } from '@/features/credits';

import FeedSheetLayout from '../FeedSheetLayout';
import getSheetCopy from './getSheetCopy';

import type { FeedInputsSheetProps } from './types';

const selector = (store: IAppStore) => ({
  inputs: store.inputs,
  presetType: store.selectedPreset?.type,
  flowId: store.selectedPreset?.flowId,
});

const FeedInputsSheet = ({
  isOpen,
  isGenerating,
  onGenerate,
  onClose,
}: FeedInputsSheetProps) => {
  const { inputs, presetType, flowId } = useAppStore(selector);
  const { isAuthorized } = useAuth();
  const translate = useTranslator();
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  // The sheet can open before the slide's quote lands (the cost is fetched, not
  // shipped with the preset), so Generate waits for it — same rule as the feed
  // pill behind the sheet and `CreateFooter` on desktop. Reads the cached quote
  // the bar already requested for this flow, so no extra call.
  const { isLoading: areCreditsLoading } = useProjectCredits({
    flowId,
    enabled: isAuthorized,
  });

  const { title, subtitle } = getSheetCopy({ inputs, presetType });
  const showSignIn = !isAuthorized;

  return (
    <FeedSheetLayout
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      primaryLabel={translate(showSignIn ? 'Sign in to generate' : 'Generate')}
      isPrimaryDisabled={
        !(showSignIn || (isFormValid(inputs) && !areCreditsLoading))
      }
      isPrimaryLoading={isGenerating}
      onPrimaryAction={onGenerate}
      primaryTestId="preset-feed-inputs-sheet-generate"
      cancelTestId="preset-feed-inputs-sheet-cancel"
      ariaLabel={title}
    >
      <InputsSection uploadSectionRef={uploadSectionRef} />
    </FeedSheetLayout>
  );
};

export default FeedInputsSheet;
